import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ResponsiveModal } from "../ResponsiveModal/ResponsiveModal";
import { BinderVariant } from "@/types/binders";
import { usePriceInput } from "@/utils/usePriceInput";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { removeVariantFromBinder } from "@/api/binders";
import { invalidateBinderWatchlistVariantsQueries } from "@/queries/common";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  binderItem: BinderVariant;
}

export function DeleteBindeItemDialog({
  open,
  onOpenChange,
  binderItem,
}: Props) {
  const [action, setAction] = useState<"sold" | "traded" | "other">("other");

  const [saleAmount, setSaleAmount] = usePriceInput("", {
    min: 0.01,
  });

  const [tradedFor, setTradedFor] = useState<string>("");

  const card = binderItem.variant.card;

  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    async mutationFn() {
      await removeVariantFromBinder(binderItem.binder_id, binderItem.id, {
        cause: action,
        payload: action === "sold" ? saleAmount : tradedFor,
      });
    },
    onSuccess() {
      onOpenChange(false);
      invalidateBinderWatchlistVariantsQueries();
    },
  });

  const isDisabled = isPending;

  const handleConfirm = async () => {
    if (!card) return;

    await mutateAsync();
  };

  useEffect(() => {
    if (open) {
      setAction("other");
      setSaleAmount("");
      setTradedFor("");
    }
  }, [open, setSaleAmount]);

  if (!card) return null;

  return (
    <ResponsiveModal open={open} onOpenChange={onOpenChange} title="Remove">
      <div className="grid gap-4">
        <p>
          Are you sure you want to remove "{card?.name ?? "this card"}" from
          this binder?
        </p>

        <div className="space-y-4 py-4" onClick={(e) => e.stopPropagation()}>
          <div>
            <Label className="text-base font-medium mb-3 block">
              Did you sell or trade your card?
            </Label>
            <RadioGroup
              value={action}
              onValueChange={(value) =>
                setAction(value as "sold" | "traded" | "other")
              }
              onClick={(e) => e.stopPropagation()}
              disabled={isDisabled}
            >
              <div
                className="flex items-center space-x-2"
                onClick={(e) => e.stopPropagation()}
              >
                <RadioGroupItem
                  value="sold"
                  id="sold"
                  onClick={(e) => e.stopPropagation()}
                  disabled={isDisabled}
                />
                <Label htmlFor="sold" onClick={(e) => e.stopPropagation()}>
                  I sold it
                </Label>
              </div>
              <div
                className="flex items-center space-x-2"
                onClick={(e) => e.stopPropagation()}
              >
                <RadioGroupItem
                  value="traded"
                  id="traded"
                  onClick={(e) => e.stopPropagation()}
                  disabled={isDisabled}
                />
                <Label htmlFor="traded" onClick={(e) => e.stopPropagation()}>
                  I traded it
                </Label>
              </div>
              <div
                className="flex items-center space-x-2"
                onClick={(e) => e.stopPropagation()}
              >
                <RadioGroupItem
                  value="other"
                  id="other"
                  onClick={(e) => e.stopPropagation()}
                  disabled={isDisabled}
                />
                <Label htmlFor="other" onClick={(e) => e.stopPropagation()}>
                  Other reason
                </Label>
              </div>
            </RadioGroup>
          </div>

          {action === "sold" && (
            <div onClick={(e) => e.stopPropagation()}>
              <Label htmlFor="saleAmount" className="font-medium">
                Sale Amount ($)
              </Label>
              <Input
                id="saleAmount"
                value={saleAmount}
                onChange={(e) => setSaleAmount(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1"
                disabled={isDisabled}
              />
            </div>
          )}

          {action === "traded" && (
            <div onClick={(e) => e.stopPropagation()}>
              <Label htmlFor="tradedFor" className="text-sm font-medium">
                What card did you trade for?
              </Label>
              <Input
                id="tradedFor"
                type="text"
                placeholder="Card name"
                value={tradedFor}
                onChange={(e) => setTradedFor(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mt-1"
                disabled={isDisabled}
              />
            </div>
          )}
        </div>

        <Button
          variant="destructive"
          onClick={handleConfirm}
          disabled={isDisabled}
        >
          Remove Card
        </Button>
      </div>
    </ResponsiveModal>
  );
}
